import { useMemo, useState } from 'react'
import { postDataAPI } from '../../../lib/api'
import OnboardingShell from './OnboardingShell'
import type { SignupUser } from '../SignupPage'
import { useNavigate } from 'react-router-dom'
import Card from '../../../components/ui/Card'
import { OnboardingStep } from './steps'

export default function Step07TeamJoin({
  setStep,
  user,
  setUser,
  from,
}: {
  setStep: (n: number) => void
  user: SignupUser & { availableArtists?: any[] }
  setUser: React.Dispatch<React.SetStateAction<SignupUser>>
  from: string
}) {
  const navigate = useNavigate()
  const [selected, setSelected] = useState<any | null>(null)
  const [isJoining, setIsJoining] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const invitedArtists = useMemo(
    () => (Array.isArray((user as any).availableArtists) ? (user as any).availableArtists : []),
    [user]
  )

  const role = user.professionRole === 'manager' || user.professionRole === 'support' ? user.professionRole : 'support'
  const roleLabel = role === 'manager' ? 'Manager' : 'Support'

  const joinAndCreateAccount = async () => {
    if (!user.email) return
    if (!selected) return
    setError(null)
    setIsJoining(true)
    try {
      const joinRes = await postDataAPI('team/join-artist', {
        teamMemberEmail: user.email,
        artistIds: [selected._id || selected.id].filter(Boolean),
        teamMemberPlan: (user as any).teamMemberPlan || (role === 'manager' ? 'basic' : 'support'),
      })
      if (joinRes?.status !== 200) throw new Error(joinRes?.message || 'Failed to join team')

      const userToSubmit: any = {
        email: user.email,
        professionRole: role,
        teamMemberPlan: (user as any).teamMemberPlan || (role === 'manager' ? 'basic' : 'support'),
        cover: null,
        avatar: null,
        selectedArtists: [selected],
      }

      const createRes = await postDataAPI('auth/add_user_info', userToSubmit)
      if (createRes?.status !== 200) throw new Error(createRes?.message || 'Failed to create account')

      const createdUser = createRes?.data?.user
      const token = createRes?.data?.token
      if (token) localStorage.setItem('dizeeToken', JSON.stringify(token))
      if (createdUser) localStorage.setItem('dizeeUser', JSON.stringify(createdUser))

      localStorage.setItem('selectedArtist', JSON.stringify(selected))

      setUser((prev) => ({ ...prev, selectedArtists: [selected] } as any))
      navigate(from || '/overview', { replace: true })
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to join team')
    } finally {
      setIsJoining(false)
    }
  }

  return (
    <OnboardingShell
      title={`You’ve been invited as a ${roleLabel}`}
      subtitle="Choose the artist you were invited to support."
      footer="If you don’t see the artist here, ask them to invite this email from Team Management."
    >
      <div className="space-y-4">
        {invitedArtists.length ? (
          <div className="max-h-[45vh] overflow-y-auto space-y-2 pr-1">
            {invitedArtists.map((a: any) => {
              const key = a._id || a.id || a.email || a.name
              const active = selected?._id === a._id
              return (
                <button key={key} type="button" onClick={() => setSelected(a)} className="w-full text-left">
                  <Card className={`transition ${active ? 'border-gray-900' : ''}`}>
                    <div className="text-sm text-gray-900">{a.name || a.username || a.email}</div>
                    <div className="text-sm text-gray-500 mt-1">{a.email || ''}</div>
                  </Card>
                </button>
              )
            })}
          </div>
        ) : (
          <div className="text-sm text-gray-500">No invitations found for this email.</div>
        )}

        {error ? <div className="text-red-600 text-sm">{error}</div> : null}

        <div className="flex gap-4 pt-2">
          <button type="button" onClick={() => setStep(OnboardingStep.VerifyOtp)} className="ds-btn ds-btn-secondary flex-1">
            Go back
          </button>
          <button
            type="button"
            disabled={!selected || isJoining || invitedArtists.length === 0}
            onClick={joinAndCreateAccount}
            className="ds-btn ds-btn-primary flex-1"
          >
            {isJoining ? 'Joining…' : 'Continue'}
          </button>
        </div>
      </div>
    </OnboardingShell>
  )
}

